import http from '@/http'
import GroupChat from '@/store/models/GroupChat'
import GroupChatPost from '@/store/models/GroupChatPost'
import { serializeObjectToQueryParameter } from '@/store/helper'

const API_PATH = 'organizations/:organizationId/group_chats/:groupChatId/group_chat_posts/'

const configureApiPath = ({ organizationId, groupChatId }) => {
  return API_PATH.replace(':organizationId', organizationId)
                 .replace(':groupChatId', groupChatId)
}

const defaultState = () => {
  return {
    currentGroupChatId: null,
    pagination: {}
  }
}

const state = defaultState()

const getters = {
  pagination: state => state.pagination
}

const mutations = {
  currentGroupChatId(state, payload) {
    state.currentGroupChatId = payload
  },
  pagination(state, payload) {
    state.pagination = payload
  },
  resetModule(state) {
    Object.assign(state, defaultState())
  }
}

const actions = {
  async index({ commit, dispatch, rootGetters }, payload) {
    const { groupChatId } = payload
    const apiPath = configureApiPath({
      organizationId: rootGetters['auth/currentOrganization'].id,
      groupChatId: groupChatId
    })
    commit('currentGroupChatId', groupChatId)
    await http.get(apiPath + serializeObjectToQueryParameter(payload)).then(response => {
      commit('pagination', response.data.pagination)
      GroupChatPost.insert({
        data: response.data.data.map((groupChatPost) => {
          let groupChatAttachmentsAttributes
          if (groupChatPost.relationships.groupChatAttachments) {
            const groupChatAttachmentIds = groupChatPost.relationships.groupChatAttachments.data.map(groupChatAttachment => groupChatAttachment.id)
            groupChatAttachmentsAttributes = response.data.included.filter(resource => resource.type === 'groupChatAttachment' && groupChatAttachmentIds.includes(resource.id))
                                                                   .map(groupChatAttachment => groupChatAttachment.attributes)
          }
          let groupChatReactions
          if (groupChatPost.relationships.groupChatReactions) {
            const groupChatReactionIds = groupChatPost.relationships.groupChatReactions.data.map(groupChatReaction => groupChatReaction.id)
            groupChatReactions = response.data.included.filter(resource => resource.type === 'groupChatReaction' && groupChatReactionIds.includes(resource.id))
                                                       .map(groupChatReaction => groupChatReaction.attributes)
          }
          return Object.assign(groupChatPost.attributes, {
            user: response.data.included.filter(resource => resource.type === 'user' && resource.id == groupChatPost.attributes.userId)[0]?.attributes,
            groupChatAttachmentsAttributes: groupChatAttachmentsAttributes,
            groupChatReactions: groupChatReactions
          })
        })
      })
      if (response.data.pagination.current === 1) {
        const currentGroupChat = GroupChat.find(groupChatId)
        if (!response.data.data.length) {
          dispatch('userGroupChatStatus/update', {
            groupChatId: groupChatId,
            attributes: {
              groupChatPostId: null
            }
          }, { root: true })
        } else {
          const newestGroupChatPostId = response.data.data.map((groupChatPost) => groupChatPost.id)
                                                          .reduce((a, b) => Math.max(a, b))
          if (currentGroupChat && currentGroupChat.meta.groupChatPostId != newestGroupChatPostId) {
            dispatch('userGroupChatStatus/update', {
              groupChatId: groupChatId,
              attributes: {
                groupChatPostId: newestGroupChatPostId
              }
            }, { root: true })
          }
        }
      }
    }).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  async create({ commit, dispatch, rootGetters }, payload) {
    const { groupChatId, attributes } = payload
    const apiPath = configureApiPath({
      organizationId: rootGetters['auth/currentOrganization'].id,
      groupChatId: groupChatId
    })
    const params = JSON.stringify({
      groupChatPost: attributes
    })
    await http.post(apiPath, params).then(response => {
      let groupChatAttachmentsAttributes
      if (response.data.data.relationships.groupChatAttachments) {
        const groupChatAttachmentIds = response.data.data.relationships.groupChatAttachments.data.map(groupChatAttachment => groupChatAttachment.id)
        groupChatAttachmentsAttributes = response.data.included.filter(resource => resource.type === 'groupChatAttachment' && groupChatAttachmentIds.includes(resource.id))
                                                               .map(groupChatAttachment => groupChatAttachment.attributes)
      }
      GroupChatPost.insert({
        data: Object.assign(response.data.data.attributes, {
          user: response.data.included.filter(resource => resource.type === 'user' && resource.id == response.data.data.attributes.userId)[0].attributes,
          groupChatAttachmentsAttributes: groupChatAttachmentsAttributes
        })
      })
      dispatch('userGroupChatStatus/update', {
        groupChatId: groupChatId,
        attributes: {
          groupChatPostId: response.data.data.id
        }
      }, { root: true })
    }).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  // NOTE: VuexORMのupdateを名前衝突を起こすのでmodule prefixで対応
  async moduleUpdate({ commit, rootGetters }, payload) {
    const { groupChatId, attributes } = payload
    const apiPath = configureApiPath({
      organizationId: rootGetters['auth/currentOrganization'].id,
      groupChatId: groupChatId
    })
    const params = JSON.stringify({
      groupChatPost: attributes
    })
    await http.put(apiPath + attributes.id, params).then(response => {
      let groupChatAttachmentsAttributes
      if (response.data.data.relationships.groupChatAttachments) {
        const groupChatAttachmentIds = response.data.data.relationships.groupChatAttachments.data.map(groupChatAttachment => groupChatAttachment.id)
        groupChatAttachmentsAttributes = response.data.included.filter(resource => resource.type === 'groupChatAttachment' && groupChatAttachmentIds.includes(resource.id))
                                                               .map(groupChatAttachment => groupChatAttachment.attributes)
      }
      let groupChatReactions
      if (response.data.data.relationships.groupChatReactions) {
        const groupChatReactionIds = response.data.data.relationships.groupChatReactions.data.map(groupChatReaction => groupChatReaction.id)
        groupChatReactions = response.data.included.filter(resource => resource.type === 'groupChatReaction' && groupChatReactionIds.includes(resource.id))
                                                   .map(groupChatReaction => groupChatReaction.attributes)
      }
      GroupChatPost.insert({
        data: Object.assign(response.data.data.attributes, {
          user: response.data.included.filter(resource => resource.type === 'user' && resource.id == response.data.data.attributes.userId)[0].attributes,
          groupChatAttachmentsAttributes: groupChatAttachmentsAttributes,
          groupChatReactions: groupChatReactions
        })
      })
    }).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  async destroy({ commit, rootGetters }, payload) {
    const { groupChatId, id } = payload
    const apiPath = configureApiPath({
      organizationId: rootGetters['auth/currentOrganization'].id,
      groupChatId: groupChatId
    })
    await http.delete(apiPath + id).then(() => {
      GroupChatPost.delete(id)
    }).catch(error => {
      commit('setHttpError', error, { root: true })
    })
  },
  deleteCurrentGroupChatId({ commit }) {
    commit('currentGroupChatId', null)
  },
  reloadCurrentIndex({ dispatch, state }, groupChatId) {
    if (state.currentGroupChatId != groupChatId) return
    dispatch('index', {
      groupChatId: groupChatId,
      page: state.pagination.current
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
